import { useState } from "react";
import { api, formatTRY, toNum } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Clock, Wallet, PlusCircle, XCircle } from "lucide-react";
import { toast } from "sonner";

const elapsed = (iso) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  return m < 60 ? `${m} dk` : `${Math.floor(m / 60)} sa ${m % 60} dk`;
};

export const OpenTabs = ({ tabs, cartItems, cartDiscount, onChanged, onCartConsumed }) => {
  const [settle, setSettle] = useState(null);
  const [pay, setPay] = useState("Nakit");
  const [amount, setAmount] = useState("");
  const [cash, setCash] = useState("");
  const [card, setCard] = useState("");
  const [editTab, setEditTab] = useState(null);
  const [rows, setRows] = useState([]);

  const openEdit = (t) => { setEditTab(t); setRows(t.items.map((i) => ({ name: i.product_name, quantity: String(i.quantity), unit_price: String(i.unit_price) }))); };
  const editSubtotal = rows.reduce((a, r) => a + toNum(r.quantity) * toNum(r.unit_price), 0);
  const saveEdit = async () => {
    try {
      const r = await api.put(`/sales/${editTab.id}/items`, { items: rows.map((x) => ({ quantity: Math.max(0, Math.round(toNum(x.quantity))), unit_price: toNum(x.unit_price) })) });
      toast.success(`Hesap güncellendi — yeni toplam ${formatTRY(r.data.total)}`);
      setEditTab(null); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Güncellenemedi"); }
  };

  const addCart = async (t) => {
    if (cartItems.length === 0) return toast.error("Sepet boş — önce ürün ekleyin");
    try {
      const r = await api.post(`/sales/${t.id}/items`, {
        items: cartItems.map((it) => ({ recipe_id: it.id, quantity: it.qty, flavor: it.flavor || "" })),
        discount_type: cartDiscount?.type || null, discount_value: cartDiscount?.value || 0,
      });
      toast.success(`Hesaba eklendi — yeni toplam ${formatTRY(r.data.total)}${r.data.added_discount > 0 ? ` (eklenen ürünlere indirim −${formatTRY(r.data.added_discount)})` : ""}`);
      if (r.data.stock_warnings?.length) toast.warning(`${r.data.stock_warnings.length} malzeme eksiye düştü`);
      onCartConsumed(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Eklenemedi"); }
  };

  const cancel = async (t) => {
    if (!window.confirm(`${t.customer_name || "Açık hesap"} iptal edilsin mi? Stok iade edilir.${t.paid_amount > 0 ? ` Alınan ${formatTRY(t.paid_amount)} ara ödeme kaydı da silinir.` : ""}`)) return;
    try { await api.delete(`/sales/${t.id}`); toast.success("Açık hesap iptal edildi"); onChanged(); }
    catch { toast.error("İptal edilemedi"); }
  };

  const openSettle = (t) => { setSettle(t); setPay("Nakit"); setAmount(String(t.remaining)); setCash(""); setCard(""); };
  const remaining = settle?.remaining || 0;
  const payNow = pay === "Karma" ? toNum(cash) + toNum(card) : toNum(amount);
  const payOk = payNow > 0 && payNow <= remaining + 0.001;

  const doSettle = async () => {
    const body = pay === "Karma" ? { payment_type: pay, cash_amount: toNum(cash), card_amount: toNum(card) } : { payment_type: pay, amount: toNum(amount) };
    try {
      const r = await api.post(`/sales/${settle.id}/settle`, body);
      toast.success(r.data.partial ? `Ara ödeme alındı ${formatTRY(payNow)} — kalan ${formatTRY(r.data.remaining)}` : `Hesap kapatıldı — ${formatTRY(settle.total)} (${r.data.payment_type})`);
      setSettle(null); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Ödeme kaydedilemedi"); }
  };

  if (tabs.length === 0) return null;
  const total = tabs.reduce((a, t) => a + t.remaining, 0);

  return (
    <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4" data-testid="open-tabs">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-heading text-lg font-semibold text-amber-500"><Clock className="h-5 w-5" /> Açık Hesaplar <span className="text-xs font-normal text-muted-foreground">({tabs.length} hesap · kalan)</span></h3>
        <span className="font-heading font-bold" data-testid="open-tabs-total">{formatTRY(total)}</span>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-3">
        {tabs.map((t) => (
          <div key={t.id} className="rounded-lg border border-border bg-card p-3" data-testid={`open-tab-${t.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold" data-testid={`open-tab-name-${t.id}`}>{t.customer_name || "İsimsiz hesap"}</p>
                <button type="button" className="block w-full truncate text-left text-xs text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-primary" title="Ürünleri gör / düzenle" data-testid={`open-tab-items-${t.id}`} onClick={() => openEdit(t)}>
                  {t.items.map((i) => `${i.product_name} x${i.quantity}`).join(", ")}
                </button>
                <p className="mt-0.5 text-[11px] text-muted-foreground"><Clock className="mr-1 inline h-3 w-3" />{elapsed(t.opened_at)} önce{t.created_by ? ` · ${t.created_by}` : ""}{t.discount_amount > 0 ? ` · indirim −${formatTRY(t.discount_amount)}` : ""}</p>
              </div>
              <div className="shrink-0 text-right">
                <p className="font-heading font-bold text-amber-500" data-testid={`open-tab-total-${t.id}`}>{formatTRY(t.remaining)}</p>
                {t.paid_amount > 0 && <p className="text-[10px] text-muted-foreground" data-testid={`open-tab-paid-${t.id}`}>Toplam {formatTRY(t.total)} · ödenen {formatTRY(t.paid_amount)}</p>}
              </div>
            </div>
            <div className="mt-2 flex gap-1">
              <Button size="sm" className="h-7 flex-1 text-xs" data-testid={`open-tab-settle-${t.id}`} onClick={() => openSettle(t)}><Wallet className="mr-1 h-3.5 w-3.5" /> Ödeme Al</Button>
              <Button size="sm" variant="secondary" className="h-7 text-xs" title="Sepettekileri (ve sepet indirimini) bu hesaba ekle" data-testid={`open-tab-add-${t.id}`} onClick={() => addCart(t)}><PlusCircle className="mr-1 h-3.5 w-3.5" /> Sepeti Ekle</Button>
              <Button size="icon" variant="ghost" className="h-7 w-7" title="İptal" data-testid={`open-tab-cancel-${t.id}`} onClick={() => cancel(t)}><XCircle className="h-4 w-4 text-destructive" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editTab} onOpenChange={(o) => !o && setEditTab(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="open-tab-edit-dialog">
          <DialogHeader><DialogTitle>Hesap Ürünleri — {editTab?.customer_name || "İsimsiz hesap"}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">Miktarı ve birim fiyatı değiştirin; miktar 0 yapılırsa ürün hesaptan çıkar ve stoğa iade edilir.</p>
          <div className="max-h-[55vh] space-y-2 overflow-y-auto">
            <div className="hidden grid-cols-12 gap-2 text-[11px] uppercase tracking-wide text-muted-foreground sm:grid"><span className="col-span-6">Ürün</span><span className="col-span-2 text-right">Adet</span><span className="col-span-2 text-right">Birim ₺</span><span className="col-span-2 text-right">Tutar</span></div>
            {rows.map((r, i) => (
              <div key={i} className="grid grid-cols-12 items-center gap-2 rounded-md border border-border/60 p-2 sm:border-0 sm:p-0" data-testid={`open-tab-edit-row-${i}`}>
                <span className="col-span-12 truncate text-sm font-medium sm:col-span-6">{r.name}</span>
                <Input className="col-span-4 h-9 text-right text-sm sm:col-span-2 sm:h-8" inputMode="numeric" placeholder="Adet" value={r.quantity} data-testid={`open-tab-edit-qty-${i}`} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, quantity: e.target.value } : x))} />
                <Input className="col-span-4 h-9 text-right text-sm sm:col-span-2 sm:h-8" inputMode="decimal" placeholder="Birim ₺" value={r.unit_price} data-testid={`open-tab-edit-price-${i}`} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, unit_price: e.target.value } : x))} />
                <span className="col-span-4 text-right font-mono text-sm sm:col-span-2">{formatTRY(toNum(r.quantity) * toNum(r.unit_price))}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between border-t border-border pt-3 text-sm">
            <span className="text-muted-foreground">Ara toplam{editTab?.discount_amount > 0 ? ` · indirim −${formatTRY(editTab.discount_amount)}` : ""}</span>
            <span className="font-heading text-lg font-extrabold" data-testid="open-tab-edit-total">{formatTRY(Math.max(0, editSubtotal - (editTab?.discount_amount || 0)))}</span>
          </div>
          <DialogFooter><Button data-testid="open-tab-edit-save" onClick={saveEdit}>Kaydet</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!settle} onOpenChange={(o) => !o && setSettle(null)}>
        <DialogContent className="sm:max-w-md" data-testid="settle-dialog">
          <DialogHeader><DialogTitle>Ödeme Al — {settle?.customer_name || "İsimsiz hesap"}</DialogTitle></DialogHeader>
          <div className="flex items-end justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Kalan</p>
              <p className="font-heading text-3xl font-extrabold" data-testid="settle-total">{formatTRY(remaining)}</p>
            </div>
            {settle?.paid_amount > 0 && <p className="text-xs text-muted-foreground">Toplam {formatTRY(settle.total)} · daha önce alınan {formatTRY(settle.paid_amount)}</p>}
          </div>
          <div className="flex gap-2">
            {["Nakit", "Kredi Kartı", "Karma"].map((p) => (
              <button key={p} type="button" data-testid={`settle-pay-${p}`} onClick={() => setPay(p)}
                className={`flex-1 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${pay === p ? "border-primary bg-primary/15 text-primary" : "border-border bg-secondary/40 hover:border-primary/50"}`}>{p}</button>
            ))}
          </div>
          {pay !== "Karma" ? (
            <div>
              <label className="text-xs text-muted-foreground">Tahsil edilecek tutar (₺) — kısmi ödeme için düşürün</label>
              <div className="mt-1 flex gap-2">
                <Input inputMode="decimal" value={amount} data-testid="settle-amount" onChange={(e) => setAmount(e.target.value)} className="h-9" />
                <Button type="button" variant="secondary" className="h-9 text-xs" data-testid="settle-amount-full" onClick={() => setAmount(String(remaining))}>Tamamı</Button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs text-muted-foreground">Nakit (₺)</label><Input inputMode="decimal" value={cash} data-testid="settle-cash" onChange={(e) => setCash(e.target.value)} className="mt-1 h-8" /></div>
              <div><label className="text-xs text-muted-foreground">Kredi Kartı (₺)</label><Input inputMode="decimal" value={card} data-testid="settle-card" onChange={(e) => setCard(e.target.value)} className="mt-1 h-8" /></div>
            </div>
          )}
          {payOk && payNow < remaining - 0.001 && <p className="text-xs text-amber-500" data-testid="settle-partial-note">Kısmi tahsilat: hesap {formatTRY(remaining - payNow)} kalanla açık kalır. Ciroya hesap kapanınca girer.</p>}
          {!payOk && <p className="text-xs text-destructive" data-testid="settle-invalid">Tutar 0 ile {formatTRY(remaining)} arasında olmalı</p>}
          <DialogFooter><Button data-testid="settle-confirm" disabled={!payOk} onClick={doSettle}>{payOk && payNow < remaining - 0.001 ? "Ara Ödeme Al" : "Tahsil Et ve Kapat"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
